import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { SuccessToast, ErrorToast } from 'src/libs/toast';
import dayjs from 'dayjs';
import animateScrollTo from 'animated-scroll-to';
import i18n from 'src/i18n';
import { COURSE_CATEGORY_TW } from 'src/constants';

import signInViewModel from 'src/components/Header/component/SignIn/viewModel';
import CourseService from 'src/services/course';
import CourseError from 'src/services/error/models/course';

import PurchaseModalViewModel from './PurchaseModal/viewModel';

export default class MainCardViewModel {
  @observable id;
  @observable type;
  @observable thumbnail;
  @observable isFavorite;
  @observable name;
  @observable subtitle;
  @observable category;
  @observable tags = [];
  @observable discount;
  @observable saleType;
  @observable rating = 5;
  @observable duration = 0;
  @observable popularity;
  @observable price;
  @observable notice;
  @observable isOwned;

  @observable isProcessing = false;

  @observable purchaseModalVm = new PurchaseModalViewModel();

  constructor() {
    makeObservable(this);
  }

  @action
  updateData = (data) => {
    const {
      id,
      type,
      name,
      subtitle,
      thumbnail,
      category,
      tags,
      saleType,
      // rating,
      duration,
      popularity,
      price,
      discount,
      isFavorite,
      isOwned,
      notice,
      rating
    } = data;

    this.id = id;
    this.type = type;
    this.thumbnail = thumbnail;
    this.isFavorite = isFavorite;
    this.name = name;
    this.subtitle = subtitle;
    this.category = category;
    this.tags = tags;
    this.saleType = saleType;
    this.discount = discount;
    this.rating = rating;
    this.duration = duration;
    this.popularity = String(popularity);
    this.price = price;
    this.notice = notice;
    this.isOwned = isOwned;

    // > test for helmet.
    localStorage.setItem('pageName', name);
  }

  @action
  changeIsFavorite = async (uid) => {
    try {
      if (!uid) {
        SuccessToast(i18n.t('common_login_unlock'));
        setTimeout(() => {
          signInViewModel.handleOpen();
        }, 500);
        return;
      }
      this.isProcessing = true;
      const res = await CourseService.changeIsFavorite(uid, this.id);
      runInAction(() => {
        this.isFavorite = res.data.isFavorite;
        if (this.isFavorite) {
          SuccessToast(i18n.t('course_favorite_add'));
        } else {
          ErrorToast(i18n.t('course_favorite_remove'));
        }
        this.isProcessing = false;
      });
    } catch (error) {
      CourseError.changeIsFavorite(error);
      this.isProcessing = false;
    }
  }

  @action
  onStartLearnClick = async () => {
    try {
      setTimeout(() => {
        this.scrollTo();
      }, 1);
    } catch (error) {
      console.error(error);
    }
  }

  @action
  scrollTo = () => {
    const mainContentClientHeight = document.querySelector('.mainContent').clientHeight;
    const courseDetailHeight = document.querySelector('.course-detail').clientHeight;
    if (courseDetailHeight > mainContentClientHeight) {
      // TODO: dynamic height
      animateScrollTo(mainContentClientHeight + 40);
    } else {
      animateScrollTo(0);
    }
  }

  @action
  onRatingClick = () => {
    const ratingSecY = document.querySelector('.rating').getBoundingClientRect().top;
    const bodyY = document.body.getBoundingClientRect().top;
    const headerHeight = document.querySelector('.header').getBoundingClientRect().height;
    const tabHeight = document.querySelector('.secTabs').getBoundingClientRect().height;
    const scrollY = ratingSecY - bodyY - headerHeight - tabHeight;
    // console.log(document.querySelector('.rating').getBoundingClientRect());
    // console.log(document.querySelector('.pageContainer').getBoundingClientRect());
    // console.log('ratingSecY', ratingSecY);
    animateScrollTo(scrollY);

  }

  @computed
  get totalLength() {
    const hours = Math.floor(dayjs.duration(this.duration, 'second').asHours());
    const minutes = dayjs.duration(this.duration, 'second').minutes();
    const seconds = dayjs.duration(this.duration, 'second').seconds();

    if (hours >= 1) {
      return ({
        hours: hours >= 10 ? hours : `0${hours}`,
        minutes: minutes >= 10 ? minutes : `0${minutes}`
      });
    }

    return ({
      minutes: minutes >= 10 ? minutes : `0${minutes}`,
      seconds: seconds >= 10 ? seconds : `0${seconds}`
    });

  }

  @computed
  get discountRemain() {
    const today = new Date().getTime();
    if (!this.discount) {
      return null;
    }

    if (this.discount?.startAt > today || today > this.discount?.endAt) {
      return null;
    }

    if ((this.discount?.endAt - today) / 86400000 < 7 && (this.discount?.endAt - today) / 86400000 >= 1) {
      return `${i18n.t('course_discount_until_7')}${Math.floor((this.discount?.endAt - today) / 86400000)}${i18n.t('course_discount_until_7_unit')}`;
    }

    if ((this.discount?.endAt - today) / 86400000 < 1) {
      return i18n.t('course_discount_left_today');
    }

    return `${i18n.t('course_discount_until_7up')}${dayjs(this.discount?.endAt).format('YYYY-MM-DD')}`;
  }

  @computed
  get roundRating() {
    return Math.round(this.rating * 10) / 10;
  }

  @computed
  get scoreText() {
    return this.roundRating.toString().length < 2 ? `${this.roundRating}.0` : this.roundRating;
  }

  @computed
  get tenThousands() {
    const length = this.popularity?.length;
    if (!this.popularity || length < 5) {
      return null;
    }
    const upper = this.popularity.slice(0, length - 4);
    const lower = this.popularity.slice(length - 4, length - 3);
    return ({
      upper,
      lower
    });
  }

  @computed
  get TypeCT() {
    switch (this.type) {
      case 'Video':
        return COURSE_CATEGORY_TW.video;
      case 'Audio':
        return COURSE_CATEGORY_TW.audio;
      case 'Audiobook':
        return COURSE_CATEGORY_TW.audiobook;
      default:
        return null;
    }
  }

  @computed
  get lowerType() {
    switch (this.type) {
      case 'Video':
        return 'video';
      case 'Audio':
        return 'audio';
      case 'Audiobook':
        return 'audiobook';
      default:
        return null;
    }
  }
}
